import { AnalyticsService } from '@alison/analytics/client';
import RestartAltOutlined from '@mui/icons-material/RestartAltOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { BoardScope } from '../BoardPage';
import { DisplayedMetrics as DisplayedMetricsPicker } from '../creatives/DisplayedMetrics';
import { downloadCsv } from '../creatives/data/csv';
import {
  type DisplayedMetrics as Displayed,
  ELEMENTS_EXPLORER_DEFAULT_DISPLAYED_METRICS,
  ensureMetricFirst,
  preferredSortMetric,
  resolveDisplayed,
  setSortMetric,
} from '../creatives/data/displayedMetrics';
import { ExportCsvButton } from '../creatives/ExportCsvButton';
import { TOOLBAR_ICON_BUTTON_SX } from '../creatives/utils';
import { type ElementsTableBase, useElementsTable, useMetricCatalog } from './data/api';
import { elementsToCsv } from './data/csv';
import { buildElementsTotals, buildElementTree, type Scene } from './data/elementsTable';
import { ElementsTable } from './ElementsTable';
import { ElementsTableGrid } from './ElementsTableGrid';
import { HowElementsExplorerWorks } from './HowElementsExplorerWorks';

type ElementsExplorerPageProps = Pick<BoardScope, 'accountIds' | 'dateWindow'> & {
  filters: Record<string, string[]> | undefined;
  leadingKpi: string;
};

export function ElementsExplorerPage({
  accountIds,
  dateWindow: dateRange,
  filters,
  leadingKpi,
}: ElementsExplorerPageProps): ReactNode {
  const [activeBase, setActiveBase] = useState<ElementsTableBase>('all');
  const [activeScenes, setActiveScenes] = useState<Array<Exclude<Scene, 'all'>>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [displayed, setDisplayedState] = useState<Displayed>(() =>
    resolveDisplayed(undefined, undefined, ELEMENTS_EXPLORER_DEFAULT_DISPLAYED_METRICS),
  );
  // accountIds hydrates in stages (empty → partial → full), so the catalog can resolve
  // more than once. Keep re-deriving from DEFAULT_DISPLAYED_METRICS on every catalog
  // change until the user actually picks something themselves — otherwise an early,
  // incomplete catalog can lock in filler metrics permanently.
  const userCustomizedMetrics = useRef(false);

  const catalog = useMetricCatalog(accountIds);

  useEffect(() => {
    if (catalog.data === undefined) return;
    setDisplayedState((current) =>
      resolveDisplayed(
        catalog.data,
        userCustomizedMetrics.current ? current : undefined,
        ELEMENTS_EXPLORER_DEFAULT_DISPLAYED_METRICS,
      ),
    );
  }, [catalog.data]);

  function setDisplayed(next: Displayed): void {
    userCustomizedMetrics.current = true;
    setDisplayedState(next);
  }

  // Functional update: shares an effect flush with the catalog-derivation effect above, and
  // reading a stale `displayed` here would let whichever effect commits last clobber the
  // other's result. Doesn't set `userCustomizedMetrics` — this is a system reorder, not a user
  // pick, and must not disable that effect's re-derivation from defaults.
  useEffect(() => {
    if (catalog.data === undefined) return;
    setDisplayedState((current) => ensureMetricFirst(current, catalog.data, leadingKpi));
  }, [leadingKpi, catalog.data]);

  const fetchMetricIds = useMemo(
    () =>
      displayed.metrics.includes(leadingKpi)
        ? displayed.metrics
        : [...displayed.metrics, leadingKpi],
    [displayed.metrics, leadingKpi],
  );

  const table = useElementsTable(
    { dateRange, accountIds, filters, metricIds: fetchMetricIds, kpi: leadingKpi },
    activeBase,
    fetchMetricIds.length > 0,
  );

  const pending = catalog.isPending || table.isPending;
  const failure = catalog.error ?? table.error;

  const scopedRows = useMemo(() => {
    if (table.data === undefined) return [];
    return selectedCategory === null
      ? table.data.rows
      : table.data.rows.filter((row) => row.category === selectedCategory);
  }, [table.data, selectedCategory]);

  const tree = useMemo(() => {
    if (catalog.data === undefined) return [];
    return buildElementTree(scopedRows, {
      activeScenes,
      catalog: catalog.data,
      metricIds: fetchMetricIds,
    });
  }, [scopedRows, catalog.data, activeScenes, fetchMetricIds]);

  const totals = useMemo(() => {
    if (catalog.data === undefined) return null;
    return buildElementsTotals(table.data?.total, fetchMetricIds);
  }, [table.data?.total, catalog.data, fetchMetricIds]);

  function resetSort(): void {
    setDisplayed(setSortMetric(displayed, catalog.data, preferredSortMetric(displayed.metrics)));
  }

  function exportCsv(): void {
    if (totals === null) return;
    downloadCsv(
      `elements_${dateRange[0]}_${dateRange[1]}.csv`,
      elementsToCsv(
        tree,
        totals,
        displayed.metrics,
        catalog.data,
        table.data?.currencySymbol ?? '',
      ),
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 5 }}>
      <Typography variant="h2" component="h1">
        Elements Explorer
      </Typography>
      <HowElementsExplorerWorks
        accountIds={accountIds}
        dateRange={dateRange}
        filters={filters}
        selectedCategory={selectedCategory}
        onSelectCategory={(category) => {
          AnalyticsService.track('Elements Explorer - Select Category', { category });
          setSelectedCategory((current) => (current === category ? null : category));
        }}
        leadingKpi={leadingKpi}
        totalCreatives={table.data?.total.creativesCount ?? null}
      />

      <ElementsTable
        activeBase={activeBase}
        onActiveBaseChange={setActiveBase}
        activeScenes={activeScenes}
        onActiveScenesChange={setActiveScenes}
        metricsControl={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DisplayedMetricsPicker
              catalog={catalog.data}
              applied={displayed}
              onChange={setDisplayed}
              surface="elements-explorer"
            />
            <Tooltip title="Reset sort">
              <IconButton size="small" onClick={resetSort} sx={TOOLBAR_ICON_BUTTON_SX}>
                <RestartAltOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <ExportCsvButton disabled={totals === null} onClick={exportCsv} />
          </Box>
        }
      >
        {failure ? (
          <Alert severity="error" sx={{ mt: 4 }}>
            {failure.message}
          </Alert>
        ) : (
          <ElementsTableGrid
            nodes={tree}
            totals={totals}
            metricIds={displayed.metrics}
            catalog={catalog.data}
            currencySymbol={table.data?.currencySymbol ?? ''}
            loading={pending}
          />
        )}
      </ElementsTable>
    </Box>
  );
}
